<template>
  <b-modal
    id="display-builder-publish-modal"
    ref="modal"
    :title="title"
    size="lg"
    no-close-on-backdrop
    @hide="close"
    @shown="handleModalShown"
  >
    <!-- Publishing Form -->
    <b-card class="mb-3">
      <b-form>
        <b-row>
          <b-col cols="12">
            <b-form-group
              label="Platform"
              label-for="platform-select"
              label-class="mb-1"
            >
              <b-form-select
                id="platform-select"
                v-model="platformId"
                size="sm"
                @change="handlePlatformChange"
              >
                <b-form-select-option
                  value=""
                  disabled
                  selected
                >
                  Select Platform
                </b-form-select-option>
                <b-form-select-option
                  v-for="platform in platforms"
                  :key="platform.id"
                  :value="platform.id"
                >
                  {{ platform.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <div class="border rounded p-3 mb-2">
        <div class="form-group row mt-2">
          <div class="col-md-4">
            <div class="d-flex align-items-center">
              <label class="mb-0 mr-2">DPI</label>
              <b-form-select
                v-model="selectedDPI"
                size="sm"
                :disabled="extension === 'webp'"
              >
                <b-form-select-option
                  v-for="dpi in dpiOptions"
                  :key="dpi"
                  :value="dpi"
                >
                  {{ dpi }}
                </b-form-select-option>
              </b-form-select>
            </div>
          </div>

          <div class="col-md-4">
            <div class="d-flex align-items-center">
              <label class="mb-0 mr-2">Format</label>
              <b-form-select
                v-model="extension"
                size="sm"
              >
                <b-form-select-option
                  v-for="format in formatOptions"
                  :key="format.value"
                  :value="format.value"
                >
                  {{ format.label }}
                </b-form-select-option>
              </b-form-select>
            </div>
          </div>

          <div
            v-if="publishToBynder && isBynderMetaAvailable"
            class="col-md-4 d-flex justify-content-end"
          >
            <b-button
              variant="outline-primary"
              size="sm"
              @click="configureMetadata"
            >
              Configure Metadata
            </b-button>
          </div>
        </div>

        <div class="form-group row px-3 mt-3">
          <label class="mb-0">Filename</label>
          <b-form-input
            v-model="optionalFilename"
            placeholder="Optional Filename"
          />
        </div>
      </div>

      <AdditionalMetaproperties
        :is-regular-publish="false"
        :is-ai-publish="false"
      />
    </b-card>

    <!-- Success Results Block -->
    <b-card
      v-if="bynderPublishResults && bynderPublishResults.length > 0"
      class="mb-3 border-success"
    >
      <div class="d-flex align-items-center mb-3">
        <h6 class="mb-0 text-success">
          Successfully Published to Platform
        </h6>
      </div>

      <div class="published-files">
        <div
          v-for="result in bynderPublishResults"
          :key="result.file_id"
          class="published-file-item mb-2 p-2 bg-light rounded"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <strong>{{ result.file_name }}</strong>
              <div
                v-if="result.media_id"
                class="text-muted small"
              >
                Asset ID: {{ result.media_id }}
              </div>
            </div>
            <div>
              <span class="badge badge-success">
                Published
              </span>
            </div>
          </div>
        </div>
      </div>
    </b-card>
    <!-- Error Results Block -->
    <b-card
      v-if="publishErrors && publishErrors.length > 0"
      class="mb-3 border-danger"
    >
      <div class="d-flex align-items-center mb-3">
        <h6 class="mb-0 text-danger">
          Publishing Errors
        </h6>
      </div>

      <div class="error-files">
        <div
          v-for="error in publishErrors"
          :key="error.file_id"
          class="error-file-item mb-2 p-2 bg-light rounded"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1">
              <strong>{{ error.file_name }}</strong>
              <div
                v-if="error.media_id"
                class="text-muted small"
              >
                Asset ID: {{ error.media_id }}
              </div>
              <div class="text-danger small mt-1">
                {{ error.error_message }}
              </div>
            </div>
            <div>
              <span class="badge badge-danger">
                Failed
              </span>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <template #modal-footer>
      <div class="w-100 d-flex justify-content-end">
        <b-button
          class="mr-2"
          variant="outline-secondary"
          @click="close"
        >
          {{
            (bynderPublishResults && bynderPublishResults.length > 0)
              ? 'Close'
              : 'Cancel'
          }}
        </b-button>
        <b-button
          v-if="isBynder"
          variant="primary"
          :disabled="isPublishing || !platformId"
          @click="handleBynderPublish"
        >
          <b-spinner
            v-if="isPublishing"
            small
            class="mr-1"
          />
          {{ isPublishing ? 'Publishing...' : actionButtonText('Bynder') }}
        </b-button>
        <b-button
          v-if="isSharepoint"
          variant="primary"
          :disabled="isPublishing || !platformId"
          @click="handleSharePointPublish"
        >
          <b-spinner
            v-if="isPublishing"
            small
            class="mr-1"
          />
          {{ isPublishing ? 'Publishing...' : actionButtonText('SharePoint') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import AdditionalMetaproperties from '@frontend/components/bynder/AdditionalMetaproperties.vue';
import {
    isBynderMetaAvailable as checkMeta,
    getPublishPreferences,
    convertDataUrlToFile
} from '@frontend/group/modules/publish-utils/publish-utils';
import {
    BYNDER_EVENTS,
    bynderEventBroker,
} from '@frontend/group/modules/bynder/event-broker';
import * as bynder from '@frontend/constants/bynder';
import { mapGetters } from 'vuex';

export default {
    name: "PublishModal",
    components: {
        AdditionalMetaproperties,
    },
    data: () => ({
        isPublishing: false,
        platformId: '',
        platforms: [],
        publishErrors: [],
        publishToBynder: false,
        publishToSharepoint: false,
        optionalFilename: null,
        isSharepoint: false,
        isBynder: false,
        bynderPublishResults: [],
        isBynderMetaAvailable: false,
        selectedPlatform: null,
        isBynderMeta: false,
        bynderMeta: null,
        platformKey: null,
        title: null,
        extension: 'png',
        selectedDPI: '300',
        dpiOptions: ['72', '75', '100', '144', '150', '200', '300'],
        formatOptions: [
            { value: 'png', label: 'PNG' },
            { value: 'jpeg', label: 'JPG' },
            { value: 'webp', label: 'WebP' },
        ],
    }),
    computed: {
        ...mapGetters('bynder', ['additionalPropertiesById']),

        aiAdditionalProperties() {
            return this.additionalPropertiesById('display-builder');
        },
    },
    watch: {
        extension(value) {
            if (value === 'webp') {
                this.selectedDPI = null;
            }
        },
    },
    mounted() {
        bynderEventBroker.on(BYNDER_EVENTS.HANDLE_META, async ({ meta }) => {
            this.isBynderMeta = true;
            this.bynderMeta = meta;
        });

        this.handleOpenPublishModal = (event) => this.openModal(event?.detail);

        window.addEventListener('display-builder:publish-modal-open', this.handleOpenPublishModal);
    },
    beforeDestroy() {
        window.removeEventListener('display-builder:publish-modal-open', this.handleOpenPublishModal);
    },
    methods: {
        openModal(platform) {
            if (platform === 'bynder') {
                this.publishToBynder = true;
                this.title = 'Publish to Bynder';
            }

            if (platform === 'sharepoint') {
                this.publishToSharepoint = true;
                this.title = 'Publish to SharePoint';
            }

            this.$bvModal.show('display-builder-publish-modal');
        },
        handleModalShown() {
            this.optionalFilename = `display-builder-${window.layoutId || Date.now()}`;
            this.getPlatforms();
        },
        async getPlatforms() {
            try {
                let platform = [];

                if (this.publishToBynder) {
                    platform.push('bynder');
                }

                if (this.publishToSharepoint) {
                    platform.push('sharepoint');
                }

                const { data } = await axios.post('/platforms/get-platforms', { platforms: platform });

                this.platforms = data.platforms || [];
            } catch (error) {
                console.error('Error fetching platforms:', error);
                toastr.error('Failed to load platforms');
            }
        },
        async handlePlatformChange() {
            this.bynderPublishResults = [];
            this.publishErrors = [];

            this.selectedPlatform = this.platforms.find(platform => platform.id == this.platformId);

            if (this.selectedPlatform) {
                const platformName = this.selectedPlatform.platform;

                switch (platformName) {
                    case 'bynder': {
                        this.isBynder = true;
                        this.isSharepoint = false;

                        this.isBynderMetaAvailable = await checkMeta();

                        this.platformKey = this.selectedPlatform.is_prod ? bynder.PLATFORM_KEY_PRODUCTION : bynder.PLATFORM_KEY_TESTING;

                        if (this.isBynderMetaAvailable) {
                            const savedMetadata = await getPublishPreferences(bynder.BYNDER_META_VALUES + this.platformKey, true);

                            if (savedMetadata) {
                                this.bynderMeta = savedMetadata;
                                this.isBynderMeta = true;
                            }

                            bynderEventBroker.fire(BYNDER_EVENTS.HANDLE_PUBLISH_ADD_PROPS, {
                                platformId: this.platformId,
                                imageMeta: null
                            });
                        }
                        break;
                    }
                    case 'sharepoint': {
                        this.isSharepoint = true;
                        this.isBynder = false;
                        break;
                    }
                    default: {
                        this.isBynder = false;
                        this.isSharepoint = false;
                        break;
                    }
                }
            }
        },
        configureMetadata() {
            bynderEventBroker.fire(BYNDER_EVENTS.PUBLISH_INIT, {
                platformId: this.platformId,
                platformKey: this.platformKey,
                assetType: this.extension,
            });
        },
        async handleBynderPublish() {
            if (!this.platformId) {
                toastr.error('Please select a platform.');
                return;
            }

            const helper = window.displayBuilderHelper;
            const canvas = helper?.canvas;
            if (!canvas) {
                toastr.error('Canvas is not available.');
                return;
            }

            this.bynderPublishResults = [];
            this.publishErrors = [];
            this.isPublishing = true;

            const filename = this.optionalFilename || `display-builder-${window.layoutId || Date.now()}`;

            try {
                const dataUrl = await helper.getExportDataUrl(this.extension, {
                    quality: 1,
                });
                const file = await convertDataUrlToFile(dataUrl, `${filename}.${this.extension}`, this.selectedDPI, this.extension);

                const formData = new FormData();

                formData.append('file', file);
                formData.append('image_type', this.extension);
                formData.append('platform_id', this.platformId);
                formData.append('file_name', filename);
                formData.append('is_bynder_meta', this.isBynderMeta ? '1' : '0');

                if (this.bynderMeta) {
                    formData.append('bynder_meta', JSON.stringify(this.bynderMeta));
                }
                if (Object.keys(this.aiAdditionalProperties).length) {
                    formData.append('additional_properties', JSON.stringify(this.aiAdditionalProperties));
                }

                const { data } = await axios.post('/bynder/file-upload', formData);

                if (data.status === 'success') {
                    this.bynderPublishResults.push({
                        file_name: filename,
                        media_id: data.data?.mediaid,
                    });

                    toastr.success('Published successfully!');
                } else {
                    this.publishErrors.push({
                        file_name: filename,
                        error_message: data.message || 'Publishing failed'
                    });

                    toastr.error(data.message || 'Failed to publish assets');
                }
            } catch (error) {
                console.error(`Error publishing:`, error);
                toastr.error(`Failed to publish assets`);
            } finally {
                this.isPublishing = false;
            }
        },
        handleSharePointPublish() {
            if (!this.platformId) {
                toastr.error('Please select a platform.');
                return;
            }

            const formData = new FormData();

            formData.append('platform_id', this.platformId);

            const { data } = axios.post('/sharepoint/file-upload', formData);
        },
        actionButtonText(platform) {
            return `Publish to ${platform}`;
        },
        resetModal() {
            this.isPublishing = false;
            this.platformId = '';
            this.platforms = [];
            this.bynderPublishResults = [];
            this.publishErrors = [];
            this.isBynderMetaAvailable = false;
            this.publishToBynder = false;
            this.publishToSharepoint = false;
            this.isBynderMeta = false;
        },
        close() {
            this.resetModal();
            this.$bvModal.hide('display-builder-publish-modal');
        }
    }
}
</script>

<style scoped>
.published-file-item {
    border-left: 3px solid #28a745;
}

.published-files {
    max-height: 300px;
    overflow-y: auto;
}

.error-file-item {
    border-left: 3px solid #dc3545;
}

.error-files {
    max-height: 300px;
    overflow-y: auto;
}
</style>
